/**
 * The Space weather pages' shared view logic (docs/specs/space-weather.md §4, §5; decisions 687, 694, 742, 749, 750;
 * R115): what Teslyn Field's Space weather screen and Teslyn Cloud's Space weather page ask the provider proxy for,
 * how often, how each answer is kept with its time, and the values both pages show, read from the held answers with
 * the parsers and rules of space-weather*.ts. Nothing here decides a level or reads a provider format itself, and
 * nothing here touches React, a browser or a clock of its own: the apps pass their storage, their proxy client and
 * the time.
 *
 * The held copies live under `teslyn.spaceweather.v1.<organisation>.<provider>.<op>[:<param>]` in each app's own
 * storage (Teslyn Field's laptop, the phone's browser), so a page opens offline with the last values and their ages.
 */
import { resolveDiurnalSettings } from './diurnal-qc.js';
import { mltWindows, surveyCentroid } from './diurnal-mlt.js';
import type { Block } from './model.js';
import type { QcRule } from './qc-spec.js';
import { DEFAULT_QC, type DiurnalUserPrefs, type ProjectOverrides } from './settings.js';
import { formatOffset } from './time-model.js';
import { ProxyError, type ProxyClient } from './tracker-feed.js';
import {
  apSoFar,
  chooseNearestK,
  daylightUtc,
  discussionItem,
  isBomFailure,
  kpForDaylight,
  latestEnlilFrame,
  nearestBomKSites,
  newestForecast,
  observedKpMax,
  parseBomAurora,
  parseBomIndex,
  parseBomMagAlert,
  parseBomMagWarning,
  parseBomPictures,
  parseDonkiEarthArrivals,
  parseDonkiNotifications,
  parseEnlilFrames,
  parseKp1m,
  parseKpForecast,
  parseKpForecastText,
  parseKpHistory,
  parseNoaaAlerts,
  parseNoaaScales,
  parseReadingItems,
  parseSolarWind24h,
  parseSolarWindNow,
  parseSpaceweatherCom,
  readingCredit,
  readingList,
  spaceweatherComItems,
  recentMagneticMessages,
  sustainedSouthwardBz,
  watchLevelFor,
  type BomAurora,
  type BomIndex,
  type BomMagAlert,
  type BomMagWarning,
  type BomPictures,
  type EarthArrival,
  type EnlilFrame,
  type KpForecastText,
  type KpPoint,
  type NoaaMessage,
  type NoaaScales,
  type ReadingItem,
  type SolarWind24h,
  type SolarWindNow,
  type SpaceweatherComNow,
} from './space-weather.js';
import { SPACE_WEATHER_SOURCES } from './space-weather-sources.js';
import { historyRuleFor } from './space-weather-history.js';
import {
  OUTLOOK_THRESHOLDS,
  diurnalLimits,
  projectSetsPlot2,
  spaceWeatherOutlook,
  stormNowFrom,
  type DiurnalLimit,
  type Outlook,
} from './space-weather-outlook.js';
import { stormAlertEvents, type StormAlertEvent } from './space-weather-alerts.js';

// ---------------------------------------------------------------------------------------------- requests

/** One operation a page asks for. */
export interface SwRequest {
  readonly provider: 'bom_sws' | 'space_weather';
  readonly op: string;
  readonly params?: Readonly<Record<string, string>>;
  /** Asked again after this long (the proxy keeps each answer at least as long for the organisation). */
  readonly everyMs: number;
}

const MIN = 60_000;

/** The request's key in the held copies: `bom_sws.k_index:Learmonth`. */
export function swKey(r: Pick<SwRequest, 'provider' | 'op' | 'params'>): string {
  const p = r.params ? Object.values(r.params).join(',') : '';
  return `${r.provider}.${r.op}${p ? `:${p}` : ''}`;
}

/** BoM K sites asked for besides the Australian region: the nearest three, so a stale nearest one has a fallback. */
export const NEAREST_SITES_ASKED = 3;

/**
 * What a page asks for (decision 687: BoM and the other "now" answers every 15 minutes; 694: the pc3 and GEOSTAT
 * pictures every 20; forecasts, solar wind history, Enlil and DONKI every 30; reading every 60). `centre`: the
 * blocks' centroid (the nearest BoM K sites).
 */
export function spaceWeatherRequests(centre: { lat: number; lon: number } | null): readonly SwRequest[] {
  const bom = (op: string, everyMs = 15 * MIN, params?: Record<string, string>): SwRequest => ({
    provider: 'bom_sws',
    op,
    everyMs,
    ...(params ? { params } : {}),
  });
  const pub = (op: string, everyMs: number): SwRequest => ({ provider: 'space_weather', op, everyMs });
  const sites = centre ? nearestBomKSites(centre).slice(0, NEAREST_SITES_ASKED) : [];
  return [
    bom('k_index', 15 * MIN, { location: 'Australian region' }),
    ...sites.map((s) => bom('k_index', 15 * MIN, { location: s.location })),
    bom('a_index', 60 * MIN),
    bom('dst_index'),
    bom('mag_alert'),
    bom('mag_warning'),
    bom('aurora_alert'),
    bom('aurora_watch'),
    bom('aurora_outlook', 60 * MIN),
    pub('noaa_scales', 15 * MIN),
    pub('noaa_kp_1m', 15 * MIN),
    pub('noaa_alerts', 15 * MIN),
    pub('noaa_solar_wind_now', 15 * MIN),
    pub('spaceweather_com', 15 * MIN),
    pub('bom_pictures', 20 * MIN),
    pub('noaa_kp', 30 * MIN),
    pub('noaa_kp_forecast', 30 * MIN),
    pub('noaa_forecast_3day', 30 * MIN),
    pub('noaa_geomag_forecast', 30 * MIN),
    pub('noaa_discussion', 30 * MIN),
    pub('noaa_solar_wind_24h', 30 * MIN),
    pub('noaa_enlil_frames', 30 * MIN),
    pub('donki_enlil', 30 * MIN),
    pub('donki_notifications', 60 * MIN),
    pub('bom_site_news', 60 * MIN),
    pub('swpc_news', 60 * MIN),
    pub('nasa_blog', 60 * MIN),
  ];
}

// ---------------------------------------------------------------------------------------------- held copies

/** A kept answer. */
export interface Held {
  readonly body: string;
  readonly fetchedAt: string;
}

/** The last state of one request. */
export interface SwEntry {
  readonly held: Held | null;
  /** The last try's failure in words, or null after a success. */
  readonly error: string | null;
  readonly code: string | null;
  /** When it was last tried (ms), so it is not asked again before its period (or a minute after a failure). */
  readonly triedAt: number | null;
}

/** The app's storage (browser storage, or a fake in tests). */
export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORE_PREFIX = 'teslyn.spaceweather.v1.';

export function storeKey(tenantId: string, key: string): string {
  return `${STORE_PREFIX}${tenantId}.${key}`;
}

/** The held copy of one request, or null. Never throws. */
export function readHeld(store: KeyValue | null, tenantId: string, key: string): Held | null {
  if (!store) return null;
  try {
    const v = JSON.parse(store.getItem(storeKey(tenantId, key)) ?? 'null') as unknown;
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.body === 'string' && typeof o.fetched_at === 'string')
        return { body: o.body, fetchedAt: o.fetched_at };
    }
  } catch {
    /* unreadable copy: treated as none */
  }
  return null;
}

/** Keep a copy. Never throws (storage full or unavailable: the page still shows the answer until it closes). */
export function writeHeld(store: KeyValue | null, tenantId: string, key: string, h: Held): void {
  try {
    store?.setItem(storeKey(tenantId, key), JSON.stringify({ body: h.body, fetched_at: h.fetchedAt }));
  } catch {
    /* storage full or unavailable */
  }
}

/** The held copies of every request, none tried yet. */
export function heldEntries(
  store: KeyValue | null,
  tenantId: string,
  requests: readonly SwRequest[],
): Record<string, SwEntry> {
  const out: Record<string, SwEntry> = {};
  for (const r of requests)
    out[swKey(r)] = { held: readHeld(store, tenantId, swKey(r)), error: null, code: null, triedAt: null };
  return out;
}

/** A failure retried after this at most (NWS: one retry a minute at most, spec §4.5). */
export const RETRY_AFTER_FAILURE_MS = MIN;

/** Whether `r` is due: never tried, its period passed since the held answer, or a minute after a failure. */
export function isDue(r: SwRequest, e: SwEntry | undefined, nowMs: number): boolean {
  if (!e || e.triedAt === null) return true;
  if (e.code === 'not_set_up' || e.code === 'forbidden') return nowMs - e.triedAt >= r.everyMs;
  if (e.error) return nowMs - e.triedAt >= Math.max(RETRY_AFTER_FAILURE_MS, Math.min(r.everyMs, 5 * MIN));
  const since = e.held ? nowMs - Date.parse(e.held.fetchedAt) : Infinity;
  return since >= r.everyMs && nowMs - e.triedAt >= RETRY_AFTER_FAILURE_MS;
}

/** A failed call in the page's words, with its code. */
export function swFailureWords(e: unknown): { readonly error: string; readonly code: string } {
  if (e instanceof ProxyError) {
    if (e.code === 'signed_out') return { error: 'Sign in to Teslyn to get space weather.', code: e.code };
    if (e.code === 'offline') return { error: 'Teslyn Cloud cannot be reached.', code: e.code };
    return { error: e.message, code: e.code };
  }
  return { error: e instanceof Error ? e.message : String(e), code: 'provider_error' };
}

/**
 * One round of requests: every due request (all of them when `force`), one at a time in the order given ("now"
 * first, so a slow source never starves the others), each answer kept in `store`; `onEntry` hears each new entry as
 * it comes; `onProgress` hears how many of the round's sources have answered (0 of n before the first is asked, then
 * each answer, failed or not, up to n of n), so a page can show a real count and bar (R168). `offline`: nothing was
 * reached because Teslyn Cloud could not be reached or the person is signed out.
 */
export async function runSpaceWeatherRound(opts: {
  readonly tenantId: string;
  readonly client: ProxyClient;
  readonly requests: readonly SwRequest[];
  readonly entries: Readonly<Record<string, SwEntry>>;
  readonly store: KeyValue | null;
  readonly now: () => number;
  readonly force?: boolean;
  readonly onEntry?: (key: string, entry: SwEntry) => void;
  readonly onProgress?: (answered: number, total: number) => void;
}): Promise<{
  readonly entries: Readonly<Record<string, SwEntry>>;
  readonly offline: boolean;
  asked: number;
}> {
  const { tenantId, client, store, now } = opts;
  const t0 = now();
  let entries: Record<string, SwEntry> = { ...opts.entries };
  const due = opts.requests.filter((r) => opts.force || isDue(r, entries[swKey(r)], t0));
  let reached = false;
  let unreachable = 0;
  let answered = 0;
  opts.onProgress?.(answered, due.length);
  for (const r of due) {
    const key = swKey(r);
    let next: SwEntry;
    try {
      const a = await client.call(r.provider, '', r.op, r.params ?? {});
      const held = { body: a.body, fetchedAt: a.fetchedAt };
      writeHeld(store, tenantId, key, held);
      next = { held, error: null, code: null, triedAt: now() };
      reached = true;
    } catch (e) {
      const w = swFailureWords(e);
      if (w.code === 'offline' || w.code === 'signed_out') unreachable++;
      else reached = true;
      next = { held: entries[key]?.held ?? null, error: w.error, code: w.code, triedAt: now() };
    }
    entries = { ...entries, [key]: next };
    opts.onEntry?.(key, next);
    answered++;
    opts.onProgress?.(answered, due.length);
  }
  return { entries, offline: due.length > 0 && !reached && unreachable > 0, asked: due.length };
}

// ---------------------------------------------------------------------------------------------- the project

/** What a page needs to know about the chosen project. */
export interface SwProjectContext {
  /** "F26048 Milgun". */
  readonly projectName: string;
  /** The blocks' centroid; null for a project without boundaries. */
  readonly centre: { readonly lat: number; readonly lon: number } | null;
  readonly offsetH: number;
  /** "UTC+8". */
  readonly zoneLabel: string;
  /** The counting Diurnal QC limits (decisions 742, 754). */
  readonly limits: readonly DiurnalLimit[];
  readonly magLatDeg: number | null;
}

/**
 * The chosen project's place, time zone and counting Diurnal QC limits, resolved as Diurnal QC resolves them
 * (`resolveDiurnalSettings`: company defaults, the contract, Project settings, and on Teslyn Field the person's own
 * Diurnal QC values, `user`); Plot 2 only when Project settings set it and it is shown (decision 742).
 */
export function spaceWeatherProjectContext(input: {
  readonly project: {
    readonly number: string;
    readonly title: string;
    readonly qc_overrides?: unknown;
    readonly qc_rules?: unknown;
  };
  readonly blocks: readonly Pick<Block, 'kind' | 'boundary' | 'epsg' | 'deleted_at'>[] | null;
  readonly user?: DiurnalUserPrefs | null;
  readonly nowMs: number;
}): SwProjectContext {
  const overrides = (input.project.qc_overrides ?? {}) as ProjectOverrides;
  const offsetH = (overrides.utcOffsetH ?? DEFAULT_QC.utcOffsetH) as number;
  const settings = resolveDiurnalSettings({
    tenant: DEFAULT_QC,
    contract: (input.project.qc_rules ?? null) as readonly QcRule[] | null,
    project: overrides,
    user: input.user ?? null,
  }).settings;
  const limits = diurnalLimits(settings, projectSetsPlot2(overrides));
  const centre = input.blocks ? surveyCentroid(input.blocks) : null;
  const today = new Date(input.nowMs).toISOString().slice(0, 10);
  return {
    projectName: `${input.project.number} ${input.project.title}`.trim(),
    centre,
    offsetH,
    zoneLabel: `UTC${formatOffset(offsetH)}`,
    limits,
    magLatDeg: centre ? mltWindows(centre.lat, centre.lon, today, offsetH).magLatDeg : null,
  };
}

/** "Milgun blocks centre 25.10° S 117.73° E". */
export function centreWords(centre: { readonly lat: number; readonly lon: number } | null): string {
  if (!centre) return '';
  return `${Math.abs(centre.lat).toFixed(2)}° ${centre.lat < 0 ? 'S' : 'N'} ${Math.abs(centre.lon).toFixed(2)}° ${centre.lon < 0 ? 'W' : 'E'}`;
}

// ---------------------------------------------------------------------------------------------- the model

export interface SwContext {
  readonly nowMs: number;
  /** The blocks' centroid; null for a project without boundaries. */
  readonly centre: { readonly lat: number; readonly lon: number } | null;
  readonly magLatDeg: number | null;
  readonly offsetH: number;
  readonly zoneLabel: string;
  readonly projectName: string;
  readonly limits: readonly DiurnalLimit[];
  /** The observatory panel's last-hour maxima, per limit, and the station code (Teslyn Field only). */
  readonly observatory: { readonly code: string; readonly maxNt: readonly number[] } | null;
}

/** A value from one source with the time its copy was fetched, and why it is missing when it is. */
export interface Part<T> {
  readonly value: T | null;
  readonly fetchedAt: string | null;
  readonly error: string | null;
  readonly code: string | null;
}

const BOM = 'bom_sws';
const PUB = 'space_weather';

function part<T>(
  entries: Readonly<Record<string, SwEntry>>,
  key: string,
  parse: (body: string) => T | null | { readonly error: string },
): Part<T> {
  const e = entries[key];
  const h = e?.held ?? null;
  let value: T | null = null;
  let error = e?.error ?? null;
  if (h) {
    const v = parse(h.body);
    if (isBomFailure(v)) error = v.error;
    else value = v as T | null;
  }
  return { value, fetchedAt: h?.fetchedAt ?? null, error, code: e?.code ?? null };
}

/** The newest of several copies' times. */
export function newest(...ts: readonly (string | null)[]): string | null {
  let best: string | null = null;
  for (const t of ts) if (t && (!best || t > best)) best = t;
  return best;
}

export interface SwModel {
  readonly outlook: Outlook;
  readonly storm: readonly StormAlertEvent[];
  readonly now: {
    readonly kAus: Part<BomIndex>;
    readonly nearest: {
      readonly site: string;
      readonly km: number;
      readonly k: BomIndex;
      readonly passedOver: readonly string[];
      readonly fetchedAt: string | null;
    } | null;
    /** The nearest site by distance, when no site's K could be used. */
    readonly nearestSiteName: string | null;
    readonly kp3h: Part<KpPoint>;
    readonly kp1m: Part<{ readonly t: string; readonly kp: number }>;
    readonly scales: Part<NoaaScales>;
    readonly dst: Part<BomIndex>;
    readonly aIndex: Part<BomIndex>;
    readonly pictures: Part<BomPictures>;
  };
  readonly alerts: {
    readonly warning: Part<BomMagWarning>;
    readonly alert: Part<BomMagAlert>;
    readonly auroras: readonly Part<BomAurora>[];
    readonly noaa: Part<readonly NoaaMessage[]>;
    readonly magnetic: readonly NoaaMessage[];
    readonly others: readonly NoaaMessage[];
  };
  readonly next: {
    readonly forecast: KpForecastText | null;
    readonly geomag: KpForecastText | null;
    readonly kpForecast: Part<readonly KpPoint[]>;
    readonly daylight: ReturnType<typeof kpForDaylight>;
    readonly watchByDay: readonly { readonly date: string; readonly g: number | null }[];
    readonly fetchedAt: string | null;
    readonly error: string | null;
  };
  readonly wind: {
    readonly now: Part<SolarWindNow>;
    readonly day: Part<SolarWind24h>;
    readonly swcom: Part<SpaceweatherComNow>;
  };
  readonly cme: {
    readonly arrivals: Part<readonly EarthArrival[]>;
    /** Each arrival with the highest observed Kp in the day after it, once it has passed. */
    readonly outcomes: readonly {
      readonly arrival: EarthArrival;
      readonly observedKp: number | null;
      /** The arrival time has passed. */
      readonly passed: boolean;
      /** A whole day has passed since it. */
      readonly dayAfter: boolean;
    }[];
    readonly frames: Part<readonly EnlilFrame[]>;
    readonly latest: EnlilFrame | null;
  };
  readonly reading: {
    readonly items: readonly ReadingItem[];
    readonly fetchedAt: string | null;
    /** The note a listed source asks for (spaceweather.com's stories, decision 689), else null. */
    readonly credit: string | null;
  };
  /** The newest copy of any K or Kp answer (the verdict's age). */
  readonly newestKFetch: string | null;
}

export function buildModel(entries: Readonly<Record<string, SwEntry>>, ctx: SwContext): SwModel {
  const k = (location: string): string => swKey({ provider: BOM, op: 'k_index', params: { location } });
  const kAus = part(entries, k('Australian region'), parseBomIndex);
  const ranked = ctx.centre ? nearestBomKSites(ctx.centre).slice(0, NEAREST_SITES_ASKED) : [];
  const siteAnswers: Record<string, BomIndex | null> = {};
  const siteFetched: Record<string, string | null> = {};
  for (const s of ranked) {
    const p = part(entries, k(s.location), parseBomIndex);
    if (p.fetchedAt) {
      siteAnswers[s.location] = p.value;
      siteFetched[s.location] = p.fetchedAt;
    }
  }
  const chosen = chooseNearestK(ranked, siteAnswers, ctx.nowMs);
  const kpHist = part(entries, `${PUB}.noaa_kp`, (b) => parseKpHistory(b));
  const kpFc = part(entries, `${PUB}.noaa_kp_forecast`, (b) => parseKpForecast(b));
  const kp3h = {
    ...kpHist,
    value: (() => {
      const all = [
        ...(kpHist.value ?? []),
        ...(kpFc.value ?? []).filter((p) => p.kind !== 'predicted'),
      ].filter((p) => Date.parse(p.t) <= ctx.nowMs);
      return all.sort((a, b) => (a.t < b.t ? 1 : -1))[0] ?? null;
    })(),
  };
  const kp1m = part(entries, `${PUB}.noaa_kp_1m`, parseKp1m);
  const scales = part(entries, `${PUB}.noaa_scales`, parseNoaaScales);
  const dst = part(entries, `${BOM}.dst_index`, parseBomIndex);
  const aIndex = part(entries, `${BOM}.a_index`, parseBomIndex);
  const pictures = part(entries, `${PUB}.bom_pictures`, parseBomPictures);

  const warning = part(entries, `${BOM}.mag_warning`, parseBomMagWarning);
  const alert = part(entries, `${BOM}.mag_alert`, parseBomMagAlert);
  const auroras = (['alert', 'watch', 'outlook'] as const).map((kind) =>
    part(entries, `${BOM}.aurora_${kind}`, (b) => parseBomAurora(kind, b)),
  );
  const noaa = part(entries, `${PUB}.noaa_alerts`, (b) => parseNoaaAlerts(b));
  const recent = recentMagneticMessages(noaa.value ?? [], ctx.nowMs);

  const f3 = part(entries, `${PUB}.noaa_forecast_3day`, parseKpForecastText);
  const fg = part(entries, `${PUB}.noaa_geomag_forecast`, parseKpForecastText);
  const forecast = newestForecast(f3.value, fg.value);
  const kpBins = forecast?.kp.length ? forecast.kp : (kpFc.value ?? []).filter((p) => p.kind !== 'observed');
  const daylight = ctx.centre ? kpForDaylight(kpBins, ctx.centre, ctx.nowMs) : null;
  const today = new Date(ctx.nowMs).toISOString().slice(0, 10);
  const days = [0, 1, 2].map((d) =>
    new Date(Date.parse(`${today}T00:00:00Z`) + d * 86_400_000).toISOString().slice(0, 10),
  );
  const watchByDay = days.map((date) => ({ date, g: watchLevelFor(noaa.value ?? [], date) }));

  const windNow = part(entries, `${PUB}.noaa_solar_wind_now`, parseSolarWindNow);
  const windDay = part(entries, `${PUB}.noaa_solar_wind_24h`, parseSolarWind24h);
  const swcom = part(entries, `${PUB}.spaceweather_com`, parseSpaceweatherCom);

  const arrivals = part(entries, `${PUB}.donki_enlil`, (b) => parseDonkiEarthArrivals(b));
  const recentArrivals = (arrivals.value ?? []).filter(
    (a) => ctx.nowMs - Date.parse(a.arrival) <= 7 * 86_400_000,
  );
  const kpObserved = [...(kpHist.value ?? []), ...(kpFc.value ?? [])];
  const outcomes = recentArrivals.map((a) => {
    const passed = Date.parse(a.arrival) <= ctx.nowMs;
    const dayAfter = Date.parse(a.arrival) + 24 * 3_600_000 <= ctx.nowMs;
    return {
      arrival: a,
      passed,
      dayAfter,
      observedKp: passed ? observedKpMax(kpObserved, a.arrival, 24) : null,
    };
  });
  const frames = part(entries, `${PUB}.noaa_enlil_frames`, (b) => parseEnlilFrames(b));

  const disc = part(entries, `${PUB}.noaa_discussion`, discussionItem);
  const donkiN = part(entries, `${PUB}.donki_notifications`, (b) => parseDonkiNotifications(b));
  const bomNews = part(entries, `${PUB}.bom_site_news`, (b) => parseReadingItems(b));
  const swpcNews = part(entries, `${PUB}.swpc_news`, (b) => parseReadingItems(b));
  const nasa = part(entries, `${PUB}.nasa_blog`, (b) => parseReadingItems(b));
  const items = readingList(
    {
      discussion: disc.value,
      swcom: spaceweatherComItems(swcom.value),
      swpc: swpcNews.value ?? [],
      bom: bomNews.value ?? [],
      donki: donkiN.value ?? [],
      nasa: nasa.value ?? [],
    },
    ctx.nowMs,
  );

  const w = warning.value;
  const warnToday = !!w && (w.startDate ?? '') <= today && (w.endDate ?? w.startDate ?? '') >= today;
  const nearestFetched = chosen ? (siteFetched[chosen.site.location] ?? null) : null;
  const newestKFetch = newest(
    kAus.fetchedAt,
    nearestFetched,
    kp1m.fetchedAt,
    kpHist.fetchedAt,
    kpFc.fetchedAt,
  );
  const outlook = spaceWeatherOutlook({
    nowMs: ctx.nowMs,
    newestFetchMs: newestKFetch ? Date.parse(newestKFetch) : null,
    magLatDeg: ctx.magLatDeg,
    kAus: kAus.value?.index ?? null,
    kNearest: chosen ? { site: chosen.site.location, k: chosen.k.index } : null,
    kpNow: kp1m.value?.kp ?? kp3h.value?.kp ?? null,
    kpDaylight: daylight?.kp ?? null,
    apToday: apSoFar(kpObserved, ctx.nowMs),
    noaaGNow: scales.value?.now.g ?? null,
    noaaWatchToday: watchLevelFor(noaa.value ?? [], today),
    bomAlertG: alert.value?.gScale ?? null,
    bomWarningToday: warnToday,
    limits: ctx.limits,
    observatory: ctx.observatory,
    southwardBz: sustainedSouthwardBz(windDay.value, ctx.nowMs),
  });
  const storm = stormAlertEvents({
    nowMs: ctx.nowMs,
    project: { name: ctx.projectName, utcOffsetH: ctx.offsetH, zoneLabel: ctx.zoneLabel },
    bomWarning: warning.value,
    bomAlert: alert.value,
    kAus: kAus.value,
    noaa: noaa.value ?? [],
    // "Storm now" where the verdict's Storm starts for this project's limits (decision 750)
    stormFrom: stormNowFrom(ctx.limits, ctx.magLatDeg),
  });
  return {
    outlook,
    storm,
    now: {
      kAus,
      nearest: chosen
        ? {
            site: chosen.site.location,
            km: chosen.site.km,
            k: chosen.k,
            passedOver: chosen.passedOver,
            fetchedAt: nearestFetched,
          }
        : null,
      nearestSiteName: ranked[0]?.location ?? null,
      kp3h,
      kp1m,
      scales,
      dst,
      aIndex,
      pictures,
    },
    alerts: { warning, alert, auroras, noaa, magnetic: recent.magnetic, others: recent.others },
    next: {
      forecast,
      geomag: fg.value,
      kpForecast: kpFc,
      daylight,
      watchByDay,
      fetchedAt: newest(f3.fetchedAt, fg.fetchedAt, kpFc.fetchedAt),
      error: f3.error ?? fg.error,
    },
    wind: { now: windNow, day: windDay, swcom },
    cme: {
      arrivals,
      outcomes,
      frames,
      latest: latestEnlilFrame(frames.value ?? [], ctx.nowMs),
    },
    reading: {
      items,
      fetchedAt: newest(
        disc.fetchedAt,
        donkiN.fetchedAt,
        bomNews.fetchedAt,
        swpcNews.fetchedAt,
        nasa.fetchedAt,
        items.some((i) => i.source === 'spaceweather_com') ? swcom.fetchedAt : null,
      ),
      credit: readingCredit(items),
    },
    newestKFetch,
  };
}

// ---------------------------------------------------------------------------------------------- page helpers

/** The Kp where "Exceedances likely" starts for the verdict's limit (the dashed line on the Kp bars). */
export function likelyKpFor(o: Pick<Outlook, 'highLatitude' | 'forLimit'>): number {
  return (
    o.highLatitude
      ? OUTLOOK_THRESHOLDS.highLatitude
      : OUTLOOK_THRESHOLDS.rules[o.forLimit ? historyRuleFor(o.forLimit) : 'chord10']
  ).likely.kp;
}

/** Daylight at the blocks for the three days from `fromMs` (UTC midnight), as [from, to] ms. */
export function daylightBands(
  centre: { readonly lat: number; readonly lon: number } | null,
  fromMs: number,
): readonly (readonly [number, number])[] {
  if (!centre) return [];
  return [0, 1, 2]
    .map((d) =>
      daylightUtc(centre.lat, centre.lon, new Date(fromMs + d * 86_400_000).toISOString().slice(0, 10)),
    )
    .filter((x): x is readonly [number, number] => x !== null);
}

/** The newest solar wind density of the 24-hour trace (the "now" summary has none). */
export function latestDensity(points: readonly { readonly density: number | null }[]): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const d = points[i]!.density;
    if (d !== null) return d;
  }
  return null;
}

/** "Mon 26 Oct" (UTC). */
export function swDay(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** The Enlil frame's address on SWPC (only SWPC's Enlil folder, as `parseEnlilFrames` checks it). */
export const ENLIL_HOST = 'https://services.swpc.noaa.gov';
export function enlilFrameUrl(f: Pick<EnlilFrame, 'path'>): string | null {
  return /^\/images\/animations\/enlil\/[A-Za-z0-9_.-]{1,120}\.jpg$/.test(f.path) && !f.path.includes('..')
    ? `${ENLIL_HOST}${f.path}`
    : null;
}

/**
 * A link a page may open: https on one of the space weather sources' own hosts (as Teslyn Field's core allows), else
 * null. Third-party addresses never reach a page any other way.
 */
export function swLink(href: string): string | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username || u.password) return null;
  const host = u.hostname.toLowerCase();
  return SPACE_WEATHER_SOURCES.some((s) => s.hosts.includes(host)) ? u.toString() : null;
}

// ---------------------------------------------------------------------------------------------- Teslyn Mobile

/**
 * What Teslyn Mobile asks for (docs/specs/mobile/teslyn-mobile.md §14): every answer the verdict, Now, the alerts and
 * warnings, the next three days, the solar wind numbers and the CME arrivals are read from, at the same periods as the
 * two pages. Left to Teslyn Cloud's page (linked from the phone): the pictures (pc3, GEOSTAT), WSA-Enlil, the
 * spaceweather.com text and the reading list, which a phone at the airstrip does not need on a weak signal.
 */
export const MOBILE_SPACE_WEATHER_OPS: ReadonlySet<string> = new Set([
  'k_index',
  'a_index',
  'dst_index',
  'mag_alert',
  'mag_warning',
  'aurora_alert',
  'aurora_watch',
  'aurora_outlook',
  'noaa_scales',
  'noaa_kp_1m',
  'noaa_alerts',
  'noaa_solar_wind_now',
  'noaa_kp',
  'noaa_kp_forecast',
  'noaa_forecast_3day',
  'noaa_geomag_forecast',
  'noaa_solar_wind_24h',
  'donki_enlil',
]);

export function mobileSpaceWeatherRequests(
  centre: { lat: number; lon: number } | null,
): readonly SwRequest[] {
  return spaceWeatherRequests(centre).filter((r) => MOBILE_SPACE_WEATHER_OPS.has(r.op));
}
